import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react';
import { calendarService } from '~/src/services/calendarService';
import type { AccountConnections, CalendarEvent } from '~/src/types/api';
import { withMicrosoftProvider } from '../helpers/calendarEventHelpers';

type UseLoadMoreCalendarEventsParams = {
  accountConnections?: AccountConnections;
  activeTab: string;
  googleNextPageToken?: string;
  microsoftNextLink?: string;
  setGoogleEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  setMicrosoftEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  setGoogleNextPageToken: Dispatch<SetStateAction<string | undefined>>;
  setMicrosoftNextLink: Dispatch<SetStateAction<string | undefined>>;
  setIsLoadingMoreGoogle: Dispatch<SetStateAction<boolean>>;
  setIsLoadingMoreMicrosoft: Dispatch<SetStateAction<boolean>>;
  googleSyncTokenRef: MutableRefObject<Record<string, string | undefined>>;
  microsoftDeltaLinkRef: MutableRefObject<Record<string, string | undefined>>;
  reportCalendarAuthError?: (provider: 'google' | 'microsoft', message?: string) => void;
};

export const useLoadMoreCalendarEvents = ({
  accountConnections,
  activeTab,
  googleNextPageToken,
  microsoftNextLink,
  setGoogleEvents,
  setMicrosoftEvents,
  setGoogleNextPageToken,
  setMicrosoftNextLink,
  setIsLoadingMoreGoogle,
  setIsLoadingMoreMicrosoft,
  googleSyncTokenRef,
  microsoftDeltaLinkRef,
  reportCalendarAuthError,
}: UseLoadMoreCalendarEventsParams) => {
  const handleLoadMoreGoogle = useCallback(async () => {
    if (!(googleNextPageToken && accountConnections?.google?.connected)) {
      return;
    }

    try {
      setIsLoadingMoreGoogle(true);
      const google = accountConnections.google;

      const page = await calendarService.fetchGoogleCalendarEventsList(
        google,
        activeTab,
        googleNextPageToken,
      );
      reportCalendarAuthError?.('google', page.authError);

      const mergedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) => ({
        ...event,
        provider: 'google',
      }));

      setGoogleEvents((prev) => [...prev, ...mergedEvents]);
      setGoogleNextPageToken(page.nextPageToken);
      if (page.nextSyncToken) {
        googleSyncTokenRef.current[activeTab] = page.nextSyncToken;
      }
    } finally {
      setIsLoadingMoreGoogle(false);
    }
  }, [
    accountConnections,
    activeTab,
    googleNextPageToken,
    googleSyncTokenRef,
    reportCalendarAuthError,
    setGoogleEvents,
    setGoogleNextPageToken,
    setIsLoadingMoreGoogle,
  ]);

  const handleLoadMoreMicrosoft = useCallback(async () => {
    if (!(microsoftNextLink && accountConnections?.microsoft?.connected)) {
      return;
    }

    try {
      setIsLoadingMoreMicrosoft(true);
      const microsoft = accountConnections.microsoft;

      const page = await calendarService.fetchMicrosoftCalendarEventsList(
        microsoft,
        activeTab,
        microsoftNextLink,
      );
      reportCalendarAuthError?.('microsoft', page.authError);

      const mergedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) =>
        withMicrosoftProvider(event),
      );

      setMicrosoftEvents((prev) => [...prev, ...mergedEvents]);
      setMicrosoftNextLink(page.nextLink);
      if (page.deltaLink) {
        microsoftDeltaLinkRef.current[activeTab] = page.deltaLink;
      }
    } finally {
      setIsLoadingMoreMicrosoft(false);
    }
  }, [
    accountConnections,
    activeTab,
    microsoftDeltaLinkRef,
    microsoftNextLink,
    reportCalendarAuthError,
    setIsLoadingMoreMicrosoft,
    setMicrosoftEvents,
    setMicrosoftNextLink,
  ]);

  const handleLoadMore = useCallback(async () => {
    const tasks: Promise<void>[] = [];

    if (googleNextPageToken) {
      tasks.push(handleLoadMoreGoogle());
    }

    if (microsoftNextLink) {
      tasks.push(handleLoadMoreMicrosoft());
    }

    if (tasks.length > 0) {
      await Promise.all(tasks);
    }
  }, [googleNextPageToken, microsoftNextLink, handleLoadMoreGoogle, handleLoadMoreMicrosoft]);

  return {
    handleLoadMore,
  };
};
